/**
 * IncomeShapeRow — SILL-537 M5 (vf-shape-editable) buyer-facing income_shape
 * control. Deliberately a PASSPORT INPUTS ROW reusing the single-scalar-
 * editor row idiom (CreditRow), NOT a Settings control (operator decision,
 * recorded 2026-08-18).
 *
 * Mirrors CreditRow's structure exactly: a collapsed summary (icon tile +
 * label + current value) with either a pencil affordance (drawer mode, when
 * `onEdit` is wired — the real production path via FinancialProfileSection)
 * or a chevron that expands the row in place to reveal the same
 * IncomeShapeEditor (fallback mode, used when no drawer host is wired).
 *
 * "Not set" handling: `snapshot.incomeShape` is nullish when the wire
 * payload didn't carry a stored value (never a fabricated default) — the
 * row renders that as an explicit "Not set" summary rather than guessing a
 * shape or silently defaulting the Select's value.
 */
import { ChevronDown, ChevronUp, Pencil, Wallet } from "lucide-react";
import { useState } from "react";

import type { DashboardSnapshot } from "../../../../lib/dashboard";
import type { UseProfileEditOptions } from "../../../../lib/profile/use-profile-edit";
import { cn } from "../../../../lib/utils";
import { TierBadge } from "../../../ui/tier-badge";
import { IncomeShapeEditor } from "./income-shape-editor";
import { incomeShapeLabel } from "./income-shape-row.helpers";
import type { ProfileRowTier } from "./tier-dot";

export function IncomeShapeRow({
  snapshot,
  opts,
  onEdit,
}: {
  snapshot: DashboardSnapshot;
  opts?: UseProfileEditOptions;
  /**
   * When provided, the row renders a pencil affordance and calls onEdit()
   * to open the Edit & Verify drawer, consistent with CreditRow. When
   * absent, the row expands in place to host the same IncomeShapeEditor.
   */
  onEdit?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const shape = snapshot.incomeShape ?? null;
  const value = shape ? incomeShapeLabel(shape) : null;

  // income_shape is always self-reported — there is no bank/document
  // verification path for "how you earn" as a category (the shape itself
  // is what DRIVES which verification path applies to the underlying
  // income/employment data, not something that gets independently
  // verified).
  const tier: ProfileRowTier = "self-reported";

  return (
    <article
      id="profile-row-income-shape"
      data-state={expanded ? "open" : "closed"}
      className="overflow-hidden p-0 transition-colors hover:bg-accent/35"
    >
      <button
        type="button"
        onClick={onEdit ? onEdit : () => setExpanded((v) => !v)}
        className="flex min-h-[4.5rem] w-full items-center justify-between gap-3 px-4 py-3.5 text-left sm:px-5"
        aria-label="How you earn"
        aria-expanded={onEdit ? undefined : expanded}
        data-slot="interactive-control"
      >
        {/* LEFT: icon tile + title + muted detail (income shape value) */}
        <span className="flex min-w-0 items-center gap-3">
          <span
            data-testid="aggregate-input-icon"
            data-tier={tier}
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-xl",
              "bg-muted text-muted-foreground",
            )}
          >
            <Wallet aria-hidden="true" className="size-4" strokeWidth={2} />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-foreground">
              How you earn
            </span>
            <span className="block truncate text-xs text-muted-foreground mt-0.5">
              {value ?? "Not set"}
            </span>
          </span>
        </span>
        {/* RIGHT: verification badge + edit affordance */}
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-2">
          <TierBadge size="sm" tier={tier} />
          {onEdit ? (
            /* Drawer mode: pencil icon opens Edit & Verify drawer */
            <span
              role="button"
              tabIndex={0}
              aria-label="Edit How you earn"
              data-slot="row-edit-trigger"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit();
                }
              }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </span>
          ) : expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </span>
      </button>

      {/* Expand-in-place mode only — suppressed when onEdit is wired */}
      {!onEdit && expanded && (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          <div className="space-y-3" data-slot="value-region">
            {opts ? (
              <IncomeShapeEditor snapshot={snapshot} opts={opts} />
            ) : (
              <p className="text-sm text-muted-foreground">
                Sign in to edit how you earn.
              </p>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
